import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from "react-router";
import { SimpleSnackbar } from "../../../../../components/snackbar";
import { Loading } from "../../../../../components/loading";
import { handlePromise } from "../../../../../utils/handlePromisse";
import { getConsultOral } from "../../../../../services/oralService";
import { OralModel } from "../../../../../models/Oral.model";
import { duplicateOralConsultation } from "../../../../../services/decisionFlow";
import DuplicateConsultDialog, { DuplicatePatientState, initialState as dupInitialState } from "../../../history/modals/duplicate/duplicate-consult.dialog";
import { generatePdfWithFeatureFlag } from "../../../../../utils/pdf.util";
import ConsultationSummary, { SummaryActive } from "../../../history/components/ConsultationSummary";
import { ProductDetailDrawer } from "../../topic/products/product-detail-drawer";
import { Details } from "../../topic/products/product-detail";
import { oralModelToPreview } from "../../components/preview/preview";

export type Consultation = {
    name: string
    actives: OralModel[]
    observations?: string[]
}

type ReviewLocationState = { patientName?: string; focus?: string; date?: string; type?: string } | null

const formatOralConc = (active: OralModel): string => {
    const volume = (active.volume ?? '').toString().trim()
    if (!volume) return '—'
    return [volume, active.unity].filter(Boolean).join(' ')
}

const deriveOralPeriod = (actives: OralModel[]): string | null => {
    const labels = new Set<string>()
    actives.forEach((a) => {
        if (a.period && a.period.trim()) labels.add(a.period.trim())
    })
    return labels.size ? Array.from(labels).join(' · ') : null
}

export const ReviewPageOral: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const locationState = (useLocation().state as ReviewLocationState) ?? null

    const [showSnackbar, setShowSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [nameConsultation, setNameConsultation] = useState<string>('')
    const [consultation, setConsultation] = useState<Consultation | null>(null)
    const [observations, setObservations] = useState<string[]>([])
    const [dupDialog, setDupDialog] = useState(dupInitialState)
    const [activeDetail, setActiveDetail] = useState<Details | null>(null)
    const [detailOpen, setDetailOpen] = useState(false)

    const onDuplicate = async (consultationId: number, patients: DuplicatePatientState[]) => {
        const [, error] = await handlePromise(duplicateOralConsultation(consultationId, patients.map((p) => p.id)))
        setDupDialog(dupInitialState)
        setSnackbarMessage(error ? (error.response?.data?.message || 'Erro ao duplicar') : 'Consulta duplicada com sucesso!')
        setShowSnackbar(true)
    }

    const fetchProducts = useCallback(async () => {
        setLoading(true)
        const [result, error] = await handlePromise(getConsultOral(id || ''))
        setLoading(false)
        if (!!error) {
            setShowSnackbar(true)
            setSnackbarMessage(error.response?.data?.message || 'Erro ao carregar a consulta')
            return
        }
        setConsultation(result.data as Consultation)
        setObservations(Array.isArray(result.data.observations) ? result.data.observations : [])
        if (result.data.name) setNameConsultation(result.data.name)
    }, [id]);

    useEffect(() => {
        fetchProducts()
    }, [fetchProducts])

    const handleRecipe = useCallback(async () => {
        await generatePdfWithFeatureFlag(Number(id) || 0, 'ORAL', setPdfLoading, setSnackbarMessage, setShowSnackbar);
    }, [id]);

    const oralActives = consultation?.actives || []
    const actives: SummaryActive[] = oralActives.map((a) => ({
        name: a.name || '—',
        fn: a.mechanism || (a.classifications?.[0] ?? ''),
        conc: formatOralConc(a),
        detail: oralModelToPreview(a),
    }))

    const handleViewActive = (active: SummaryActive) => {
        if (!active.detail) return
        setActiveDetail(active.detail)
        setDetailOpen(true)
    }

    const focus = locationState?.focus || nameConsultation

    return (
        <>
            <Loading open={pdfLoading} />
            <DuplicateConsultDialog
                open={dupDialog.open}
                consult={dupDialog.consult}
                onClose={() => setDupDialog(dupInitialState)}
                onDuplicate={onDuplicate}
            />
            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <ConsultationSummary
                title={locationState?.patientName || nameConsultation}
                focus={focus}
                date={locationState?.date}
                typeLabel="Oral"
                vehicle={null}
                period={deriveOralPeriod(oralActives)}
                actives={actives}
                observations={observations}
                loading={loading}
                onBack={() => navigate('/dashboard/history')}
                onDuplicate={() => setDupDialog({ open: true, consult: { id: Number(id) || 0, date: locationState?.date || '', name: focus, type: 'ORAL' } })}
                onRecipe={handleRecipe}
                onViewActive={handleViewActive}
            />

            <ProductDetailDrawer
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                detail={activeDetail ?? ({} as Details)}
                typeLabel="Oral"
            />
        </>
    )
}